/**
 * Your VAULT Journey — restrained milestone timeline on /profile.
 *
 * Organisation signals only. Never renders wealth / credit / balances.
 * A subtle gold ring animates around L2/L3 achievements the first
 * time they're seen; the ring collapses to a static check thereafter.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CheckCircle2, Sparkles, ArrowRight } from "lucide-react";

const LEVEL_BADGE = {
    1: "bg-stone-100 text-stone-600 border-stone-200",
    2: "bg-amber-50 text-amber-800 border-amber-200",
    3: "bg-gradient-to-r from-amber-100 to-amber-50 text-amber-900 border-amber-300",
};

export default function VaultJourney() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/milestones/journey")
            .then((r) => setData(r.data))
            .catch(() => setData({ achieved: [], total_possible: 17, total_achieved: 0 }))
            .finally(() => setLoading(false));
    }, []);

    const markSeen = async (id) => {
        try { await api.post(`/milestones/${id}/seen`); } catch { /* silent */ }
    };

    if (loading) {
        return <div className="p-8 text-stone-500" data-testid="journey-loading">Loading your journey…</div>;
    }
    const achieved = data?.achieved || [];
    const pct = data?.total_possible
        ? Math.round((data.total_achieved / data.total_possible) * 100) : 0;

    return (
        <section className="max-w-3xl mx-auto px-4 md:px-8 py-8 space-y-6" data-testid="vault-journey-page">
            <header>
                <div className="text-[11px] uppercase tracking-[0.24em] text-stone-500 font-semibold">Your VAULT</div>
                <h1 className="text-3xl md:text-4xl font-bold text-stone-900 mt-1">Your VAULT Journey</h1>
                <p className="text-stone-600 mt-2 max-w-lg">
                    Meaningful moments in getting your financial life organised. Nothing about wealth
                    or credit — only how much you&rsquo;ve brought together in one calm place.
                </p>
            </header>

            {/* Progress ring */}
            <div className="rounded-2xl border border-stone-200 bg-white p-6 flex items-center gap-5" data-testid="journey-progress">
                <ProgressRing pct={pct} />
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-stone-500 font-semibold">Organisation</div>
                    <div className="text-2xl font-bold text-stone-900">{data.total_achieved} of {data.total_possible}</div>
                    <div className="text-[13px] text-stone-500 mt-0.5">milestones reached</div>
                </div>
                {data.next_up && (
                    <div className="ml-auto text-right hidden md:block" data-testid="journey-next">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-semibold">Next up</div>
                        <div className="text-sm font-semibold text-amber-800 mt-1 flex items-center gap-1">
                            {data.next_up.title} <ArrowRight size={13} />
                        </div>
                    </div>
                )}
            </div>

            {/* Achieved list */}
            {achieved.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-stone-300 bg-stone-50 p-10 text-center" data-testid="journey-empty">
                    <Sparkles size={22} className="text-stone-400 mx-auto" />
                    <p className="text-stone-600 mt-2">Your first milestone lands the moment you add anything to VAULT.</p>
                </div>
            ) : (
                <ul className="space-y-3">
                    {achieved.slice().reverse().map((m) => (
                        <li
                            key={m.id}
                            onMouseEnter={() => !m.in_app_seen_at && markSeen(m.id)}
                            className="rounded-2xl border border-stone-200 bg-white p-4 flex items-center gap-4 relative"
                            data-testid={`journey-item-${m.milestone_key}`}
                        >
                            <div className={`relative shrink-0 ${!m.in_app_seen_at && m.level >= 2 ? "animate-pulse" : ""}`}>
                                <div className={`w-9 h-9 rounded-full border ${LEVEL_BADGE[m.level] || LEVEL_BADGE[1]} flex items-center justify-center`}>
                                    <CheckCircle2 size={16} />
                                </div>
                                {!m.in_app_seen_at && m.level >= 2 && (
                                    <span className="absolute inset-0 rounded-full ring-2 ring-amber-400/70 animate-ping" />
                                )}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="font-semibold text-stone-900 truncate">{m.title}</div>
                                <div className="text-[12px] text-stone-500">
                                    {new Date(m.achieved_at).toLocaleDateString(undefined,
                                        { day: "numeric", month: "short", year: "numeric" })}
                                    {m.email_bundled && <> · Grouped in the next VAULT Progress Update</>}
                                </div>
                            </div>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${LEVEL_BADGE[m.level] || LEVEL_BADGE[1]}`}>
                                {m.level === 1 ? "MICRO" : m.level === 2 ? "MEANINGFUL" : "MAJOR"}
                            </span>
                        </li>
                    ))}
                </ul>
            )}

            <footer className="text-[12px] text-stone-500 text-center pt-4">
                Clarity. Control. Confidence. — VAULT by PD
            </footer>
        </section>
    );
}

function ProgressRing({ pct }) {
    const r = 26, c = 2 * Math.PI * r;
    const off = c - (pct / 100) * c;
    return (
        <svg width="72" height="72" viewBox="0 0 72 72" data-testid="journey-ring">
            <circle cx="36" cy="36" r={r} fill="none" stroke="#f5f5f4" strokeWidth="5" />
            <circle cx="36" cy="36" r={r} fill="none" stroke="url(#gold)" strokeWidth="5"
                    strokeDasharray={c} strokeDashoffset={off}
                    strokeLinecap="round" transform="rotate(-90 36 36)"
                    style={{ transition: "stroke-dashoffset 700ms ease" }} />
            <text x="36" y="41" textAnchor="middle" fontSize="14"
                  fontWeight="700" fill="#78716c">{pct}%</text>
            <defs>
                <linearGradient id="gold" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#c9a96b" />
                    <stop offset="100%" stopColor="#e6c78a" />
                </linearGradient>
            </defs>
        </svg>
    );
}
